import { ApolloProvider } from '@apollo/client';
import { ApproveAppDialog } from 'app-governance-lib/components/ApproveAppDialog';
import { ManagementModalPanel } from 'app-governance-lib/components/ManagementModalPanel';
import { gate } from 'hub-http/gates';
import { createApolloClient } from 'hubspot-apollo-client';
import { CheckCircleIcon } from 'foundations-assets/icons/components/CheckCircleIcon';
import { CogIcon } from 'foundations-assets/icons/components/CogIcon';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApprovalProvider } from 'ui-product-approvals/components/ApprovalProvider';
import { useGetAppGovernancePermissionsQuery, useGetAppsGovernanceStatusQuery, useUpdateAllowedEntityTypeAndIdsMutation } from '../../__generated__/chirp/com/hubspot/applications/governance/rpc/ApplicationGovernanceAppAuthRpc.hooks';
import { useGetUsersByIdsQuery } from '../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc.hooks';
import { useRejectApprovalMutation, useUpdateApprovalMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { APP_GOVERNANCE_APPROVAL_ACTION, APP_GOVERNANCE_APPROVAL_TYPE } from 'marketplace-ui-common/governance/constants';
import { useGetLatestProductApprovalRequest } from 'marketplace-ui-common/governance/hooks/useGetLatestProductApprovalRequest';
import { useGetOrCreateApprovableObjectId } from 'marketplace-ui-common/governance/hooks/useGetOrCreateApprovableObjectId';
import { useGetProductApprovalConfiguration } from 'marketplace-ui-common/governance/hooks/useGetProductApprovalConfiguration';
import { getUserHasAppGovernanceWriteScope } from './utils';
import { getApprovedAppsUrl, navigateTo } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const APP_GOVERNANCE_ALERT_SUBSCREEN = 'app governance admin alert';
const ENTERPRISE_MANAGED_AUTH_GATE = gate('Developers:AdminGovernance:EMA');
const apolloClient = createApolloClient();
function AppGovernanceAlertContent({
  appId,
  appName,
  approvableObjectId,
  currentUserId
}) {
  var _userInfo$gates, _userInfo$user, _userInfo$user2, _userData$users$;
  const {
    userInfo,
    isEmbedded,
    trackInteraction
  } = useDetailsPageContext();
  const [isAppGovernanceModalOpen, setIsAppGovernanceModalOpen] = useState(false);
  const [isAppApproved, setIsAppApproved] = useState(false);
  const isEnterpriseManagedAuthUngated = !!(userInfo !== null && userInfo !== void 0 && (_userInfo$gates = userInfo.gates) !== null && _userInfo$gates !== void 0 && _userInfo$gates.includes(ENTERPRISE_MANAGED_AUTH_GATE));
  const {
    data,
    hasLatestRequestData,
    requestStatus,
    requesterId,
    loading,
    refetch: refetchProductApprovalData
  } = useGetLatestProductApprovalRequest({
    approvableObjectId
  });
  const requesterIdAsNumber = parseInt(requesterId || '0', 10);
  const {
    data: appGovernancePermissionsData,
    loading: appGovernancePermissionsLoading,
    error: appGovernancePermissionsError,
    refetch: refetchAppGovernancePermissionsData
  } = useGetAppGovernancePermissionsQuery({
    variables: {
      appId
    }
  });
  useEffect(() => {
    // if approval exists, should use manage settings modal
    if (!appGovernancePermissionsLoading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsAppApproved(!appGovernancePermissionsError && (appGovernancePermissionsData === null || appGovernancePermissionsData === void 0 ? void 0 : appGovernancePermissionsData.approvedAt) !== undefined);
    }
  }, [appGovernancePermissionsData, appGovernancePermissionsError, appGovernancePermissionsLoading]);
  const adminEmail = userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.email;
  const adminName = userInfo === null || userInfo === void 0 || (_userInfo$user2 = userInfo.user) === null || _userInfo$user2 === void 0 ? void 0 : _userInfo$user2.first_name;
  const [rejectApproval] = useRejectApprovalMutation();
  const [updateApproval] = useUpdateApprovalMutation();
  const [updateAllowedEntityTypeAndIds] = useUpdateAllowedEntityTypeAndIdsMutation();
  const {
    data: userData,
    loading: loadingUserData
  } = useGetUsersByIdsQuery({
    variables: {
      userIds: [requesterIdAsNumber]
    },
    skip: requesterId === undefined
  });
  const isActiveRequest = useRef(requestStatus === 'APPROVAL_PENDING');
  useEffect(() => {
    // alert should only show in APPROVED and REJECTED state immediately after the admin has approved/rejected, otherwise hide
    if (
    // eslint-disable-next-line react-hooks/refs
    isActiveRequest.current === false) {
      // eslint-disable-next-line react-hooks/refs
      isActiveRequest.current = requestStatus === 'APPROVAL_PENDING';
    }
  }, [requestStatus]);
  const requesterEmail = userData === null || userData === void 0 || (_userData$users$ = userData.users[0]) === null || _userData$users$ === void 0 ? void 0 : _userData$users$.email;
  let requestAlertType = 'info';
  let requestAlertTitleKey = 'PENDING';

  // eslint-disable-next-line react-hooks/refs
  if (isActiveRequest.current) {
    switch (requestStatus) {
      case 'APPROVED':
        requestAlertType = 'success';
        requestAlertTitleKey = 'APPROVED';
        break;
      case 'REJECTED':
        requestAlertType = 'warning';
        requestAlertTitleKey = 'REJECTED';
        break;
      case 'APPROVAL_PENDING':
        requestAlertType = 'info';
        requestAlertTitleKey = isAppApproved ? 'PENDING_ACCESS' : 'PENDING_ADD';
        break;
      default:
        break;
    }
  }

  //if active request modal and missing latest request data, do not show modal
  // eslint-disable-next-line react-hooks/refs
  if (isActiveRequest.current && !hasLatestRequestData) {
    return null;
  }
  if (loading || loadingUserData) {
    return null;
  }
  function handleApproveUserForApprovedApp() {
    trackInteraction({
      subscreen: APP_GOVERNANCE_ALERT_SUBSCREEN,
      action: 'click Approve app governance request for approved app'
    });
    if (!requesterId) {
      console.error(new Error('Missing requester id, try again later'));
      return;
    }
    updateAllowedEntityTypeAndIds({
      variables: {
        appId,
        installPermissionAccessState: 'SPECIFIC_USERS_AND_TEAMS',
        entityTypeAndIds: [...((appGovernancePermissionsData === null || appGovernancePermissionsData === void 0 ? void 0 : appGovernancePermissionsData.entityTypeAndIds) || []), {
          entityType: 'USER',
          entityId: requesterIdAsNumber
        }]
      }
    }).then(() => {
      refetchProductApprovalData();
      refetchAppGovernancePermissionsData().catch(err => console.error(err));
    }).catch(error => {
      console.error(error);
      FloatingAlertStore.addAlert({
        type: 'danger',
        titleText: unescapedText('detailsPage.appGovernance.failedApprovalAlert.titleText'),
        message: unescapedText('detailsPage.appGovernance.failedApprovalAlert.message')
      });
    });
  }
  function handleOpenAppGovernanceModal() {
    setIsAppGovernanceModalOpen(true);
  }
  function handleDenyRequest() {
    var _data$approval, _data$approval2;
    trackInteraction({
      subscreen: APP_GOVERNANCE_ALERT_SUBSCREEN,
      action: 'click Deny app governance request'
    });
    const isUserInApprovalSteps = data === null || data === void 0 || (_data$approval = data.approval) === null || _data$approval === void 0 || (_data$approval = _data$approval.approvalSteps) === null || _data$approval === void 0 ? void 0 : _data$approval.some(step => {
      var _step$approver, _step$approver2;
      return ((_step$approver = step.approver) === null || _step$approver === void 0 ? void 0 : _step$approver.actorType) === 'USER' && ((_step$approver2 = step.approver) === null || _step$approver2 === void 0 ? void 0 : _step$approver2.id) === String(currentUserId);
    });
    const updateStep = isUserInApprovalSteps ? Promise.resolve() : updateApproval({
      variables: {
        approvalId: (data === null || data === void 0 || (_data$approval2 = data.approval) === null || _data$approval2 === void 0 ? void 0 : _data$approval2.id) || 0,
        approvalSteps: [{
          actor: {
            actorType: 'USER',
            id: String(currentUserId)
          }
        }]
      }
    });
    updateStep.then(() => {
      var _data$approval3;
      return rejectApproval({
        variables: {
          approvalId: (data === null || data === void 0 || (_data$approval3 = data.approval) === null || _data$approval3 === void 0 ? void 0 : _data$approval3.id) || 0,
          rejectionNote: unescapedText('detailsPage.appGovernance.rejectionNote', {
            email: adminEmail,
            name: adminName
          })
        }
      });
    }).then(() => refetchProductApprovalData()).catch(err => console.error(err));
  }
  function onAppGovernanceModalClosed() {
    setIsAppGovernanceModalOpen(false);
    refetchProductApprovalData();
    refetchAppGovernancePermissionsData().catch(err => console.error(err));
  }
  function ApproveButton() {
    return /*#__PURE__*/_jsxs(Button, {
      marginInlineStart: "200",
      use: 'secondary-alt',
      size: "small",
      onClick: isAppApproved ? handleApproveUserForApprovedApp : () => {
        trackInteraction({
          subscreen: APP_GOVERNANCE_ALERT_SUBSCREEN,
          action: 'click Approve app governance request'
        });
        handleOpenAppGovernanceModal();
      },
      children: [/*#__PURE__*/_jsx(CheckCircleIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.appGovernance.approveButton"
      })]
    });
  }
  const shouldShowConnectedAppsButton = !window.location.pathname.includes('connected-apps/');
  function ManageButtons() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Button, {
        marginInlineStart: "200",
        use: 'tertiary',
        size: "small",
        onClick: () => {
          trackInteraction({
            subscreen: APP_GOVERNANCE_ALERT_SUBSCREEN,
            action: 'click Manage app governance'
          });
          handleOpenAppGovernanceModal();
        },
        children: [/*#__PURE__*/_jsx(CogIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.appGovernance.manageButton"
        })]
      }), shouldShowConnectedAppsButton && /*#__PURE__*/_jsx(Button, {
        marginInlineStart: "200",
        use: 'tertiary',
        size: "small",
        onClick: () => {
          trackInteraction({
            subscreen: APP_GOVERNANCE_ALERT_SUBSCREEN,
            action: 'click View approved apps'
          });
          navigateTo(getApprovedAppsUrl(), isEmbedded);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.appGovernance.viewApprovedAppsButton"
        })
      })]
    });
  }
  function ActiveRequestAlert() {
    return /*#__PURE__*/_jsxs(Alert, {
      type: requestAlertType,
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.requestAlertTitle.${requestAlertTitleKey}`,
        options: {
          email: requesterEmail
        }
      }),
      marginBlockEnd: "500",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.requestAlertContent.${requestAlertTitleKey}`
      }), requestStatus === 'APPROVAL_PENDING' && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ApproveButton, {}), /*#__PURE__*/_jsx(Button, {
          use: "tertiary",
          size: "small",
          onClick: handleDenyRequest,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.appGovernance.denyButton"
          })
        })]
      }), requestStatus === 'APPROVED' && /*#__PURE__*/_jsx(ManageButtons, {})]
    });
  }
  function GenericAlert() {
    return /*#__PURE__*/_jsxs(Alert, {
      type: isAppApproved ? 'success' : 'info',
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.genericAlertTitle.${isAppApproved ? 'approved' : 'default'}`
      }),
      marginBlockEnd: "500",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.genericAlertContent.${isAppApproved ? 'approved' : 'default'}`
      }), isAppApproved ? /*#__PURE__*/_jsx(ManageButtons, {}) : /*#__PURE__*/_jsx(ApproveButton, {})]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isActiveRequest.current ? /*#__PURE__*/_jsx(ActiveRequestAlert, {}) : /*#__PURE__*/_jsx(GenericAlert, {}), isAppGovernanceModalOpen && /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: isAppApproved ? /*#__PURE__*/_jsx(ManagementModalPanel, {
        onClose: onAppGovernanceModalClosed,
        appName: appName,
        appId: appId,
        currentUserId: currentUserId,
        onInstallPermissionsSave: onAppGovernanceModalClosed,
        onRevokeApproval: onAppGovernanceModalClosed,
        isEnterpriseManagedAuthUngated: isEnterpriseManagedAuthUngated
      }) : /*#__PURE__*/_jsx(ApproveAppDialog, {
        type: "modal",
        appId: appId,
        appName: appName,
        currentUserId: currentUserId,
        onClose: onAppGovernanceModalClosed,
        onConfirm: onAppGovernanceModalClosed,
        initialUserIds:
        // eslint-disable-next-line react-hooks/refs
        isActiveRequest.current && requesterId ? [requesterIdAsNumber] : []
      })
    })]
  });
}
export function AppGovernanceAlert({
  appId,
  appName
}) {
  var _userInfo$user$user_i, _userInfo$user3;
  const {
    userInfo,
    appContext
  } = useDetailsPageContext();
  const {
    approvableObjectId
  } = useGetOrCreateApprovableObjectId({
    appId
  });
  const {
    enabled: isProductApprovalsEnabled
  } = useGetProductApprovalConfiguration({});
  const {
    data: appGovernanceStatusData
  } = useGetAppsGovernanceStatusQuery({
    variables: {
      appIds: [appId]
    },
    skip: !appId
  });
  const showAppGovernanceAlert = useMemo(() => getUserHasAppGovernanceWriteScope(userInfo) && (appGovernanceStatusData === null || appGovernanceStatusData === void 0 ? void 0 : appGovernanceStatusData.appGovernanceStatuses[appId]) === 'GOVERNED', [appGovernanceStatusData === null || appGovernanceStatusData === void 0 ? void 0 : appGovernanceStatusData.appGovernanceStatuses, appId, userInfo]);
  if (!showAppGovernanceAlert || appContext !== 'app' || !isProductApprovalsEnabled || !approvableObjectId) {
    return null;
  }
  return /*#__PURE__*/_jsx(ApprovalProvider, {
    approvableObjectId: approvableObjectId,
    assetName: appName,
    approvalType: APP_GOVERNANCE_APPROVAL_TYPE,
    approvalAction: APP_GOVERNANCE_APPROVAL_ACTION,
    children: /*#__PURE__*/_jsx(AppGovernanceAlertContent, {
      appId: appId,
      appName: appName,
      approvableObjectId: approvableObjectId,
      currentUserId: (_userInfo$user$user_i = userInfo === null || userInfo === void 0 || (_userInfo$user3 = userInfo.user) === null || _userInfo$user3 === void 0 ? void 0 : _userInfo$user3.user_id) !== null && _userInfo$user$user_i !== void 0 ? _userInfo$user$user_i : 0
    })
  });
}